import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    Divider,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function SecurityAndData() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>Special-category assessment data | BetaPersona</title>
                <meta name="description" content="Psychometric data is special-category personal data under GDPR. We treat it that way. UK company. Qualified test use. Data processing agreements cover storage, access and generation." />
            </Helmet>

            <Container maxW="container.lg" py={{ base: 12, md: 20 }}>
                <VStack spacing={8} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            Psychometric data is special-category. We treat it that way.
                        </Heading>
                        <Text fontSize="xl" color="ink.500">
                            Psychometric assessment data is special-category personal data under GDPR Article 9. We process it lawfully, store it securely, and ensure qualified test use.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            Who is who
                        </Heading>
                        <VStack align="start" spacing={4}>
                            <Box>
                                <Text fontWeight="600">Data controller</Text>
                                <Text>
                                    The employer (or assessment practice, or coach). You determine purposes and means. You instruct us to store, apply access policy, and generate reports.
                                </Text>
                            </Box>
                            <Box>
                                <Text fontWeight="600">Data processor</Text>
                                <Text>
                                    Digitalfields Europe Ltd (BetaPersona). We process on your instruction under a data processing agreement.
                                </Text>
                            </Box>
                            <Box>
                                <Text fontWeight="600">Data subject</Text>
                                <Text>
                                    The person assessed. After shortlist, the employer owns the relationship. Consent or legitimate interest as lawful basis. Right to access, rectification, erasure.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            What we store
                        </Heading>
                        <VStack align="start" spacing={4}>
                            <Text>
                                • Assessment files (PDFs from publishers, or scores if integrated)
                            </Text>
                            <Text>
                                • Derived reports (generated from publisher scores and your framework)
                            </Text>
                            <Text>
                                • Access logs (who viewed what, when)
                            </Text>
                            <Text>
                                • Metadata (person identifier, role, date assessed, framework version)
                            </Text>
                            <Text mt={4}>
                                We do not store curriculum vitae, personal contact details beyond the person identifier you provide, or clinical data.
                            </Text>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            Hosting
                        </Heading>
                        <Text>
                            Hosting arrangements are documented in the data processing agreement. We do not make hosting region a marketing slogan. Different clients have different requirements.
                        </Text>
                        <Text mt={4}>
                            At your request, we will document where data is hosted, sub-processor arrangements, and backup retention. This belongs in a DPA, not a public website.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            UK company
                        </Heading>
                        <VStack align="start" spacing={3}>
                            <Text>
                                <strong>Digitalfields Europe Ltd</strong>
                            </Text>
                            <Text>Company number: 15999199</Text>
                            <Text>
                                Registered office: 85 Great Portland Street, First Floor, London W1W 7LT, United Kingdom
                            </Text>
                            <Text mt={4}>
                                UK GDPR applies. ICO registration where required. Standard contractual clauses for international transfers if applicable to your deployment.
                            </Text>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            Qualified test use
                        </Heading>
                        <Text>
                            Occupational assessment under British Psychological Society or European Federation of Psychologists' Associations standards. The in-house OP, qualified HRBP, or external assessment practice are the qualified users. BetaPersona stores, applies access policy, and generates the derived report. We do not interpret.
                        </Text>
                        <Text mt={4}>
                            The occupational psychologist still advises. We remove the collating.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={6}>
                            What a DPA will cover
                        </Heading>
                        <VStack align="start" spacing={2}>
                            <Text>• Scope of processing</Text>
                            <Text>• Data subject categories</Text>
                            <Text>• Controller instructions</Text>
                            <Text>• Sub-processors</Text>
                            <Text>• Security measures (encryption, access control, audit logs)</Text>
                            <Text>• Data subject rights (how we assist controller)</Text>
                            <Text>• Breach notification</Text>
                            <Text>• Hosting and backup arrangements</Text>
                            <Text>• Return or deletion at contract end</Text>
                            <Text>• Audits</Text>
                        </VStack>
                        <Text mt={6}>
                            Standard form DPA. Amendments by mutual agreement where your requirements differ.
                        </Text>
                    </Box>

                    <Divider my={8} />

                    <Box bg="navy.500" color="white" p={8} borderRadius="md">
                        <Heading as="h3" size="lg" mb={4}>
                            Questions about data processing?
                        </Heading>
                        <Text mb={6}>
                            We will answer security and qualified-use questions in a working session.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
